export const DragTableId = {
  G1: "G1",
  G2: "G2",
  G5: "G5",
  G6: "G6",
  G7: "G7",
  G8: "G8",
  GI: "GI",
  RA4: "RA4",
  GC: "GC",
} as const;
export type DragTableId = (typeof DragTableId)[keyof typeof DragTableId];

export interface DragTableDataPoint {
  mach: number;
  drag_coefficient: number;
}

export class DragTableNode {
  mach: number;
  drag_coefficient: number;
  a: number;
  b: number;
  c: number;

  constructor(mach: number, drag_coefficient: number, a: number, b: number, c: number) {
    this.mach = mach;
    this.drag_coefficient = drag_coefficient;
    this.a = a;
    this.b = b;
    this.c = c;
  }

  calculate_drag(mach: number): number {
    return this.c + mach * (this.b + this.a * mach);
  }
}

export class DragTable {
  public id: DragTableId;
  public nodes: DragTableNode[];

  constructor(id: DragTableId, data: DragTableDataPoint[]) {
    this.id = id;
    this.nodes = [];

    if (data.length < 2) {
      throw new Error("DragTable requires at least 2 data points");
    }

    const size = data.length;

    // First node
    const first = data[0]!;
    const second = data[1]!;
    const last = data[size - 1]!;
    const rate = (second.drag_coefficient - first.drag_coefficient) / (second.mach - first.mach);
    this.nodes.push(
      new DragTableNode(
        first.mach,
        first.drag_coefficient,
        0.0,
        rate,
        first.drag_coefficient - first.mach * rate
      )
    );

    // Middle nodes
    for (let i = 1; i < size - 1; i++) {
      const previous = data[i - 1]!;
      const current = data[i]!;
      const next = data[i + 1]!;

      const x1 = previous.mach;
      const x2 = current.mach;
      const x3 = next.mach;

      const y1 = previous.drag_coefficient;
      const y2 = current.drag_coefficient;
      const y3 = next.drag_coefficient;

      const a =
        ((y3 - y1) * (x2 - x1) - (y2 - y1) * (x3 - x1)) /
        ((x3 * x3 - x1 * x1) * (x2 - x1) - (x2 * x2 - x1 * x1) * (x3 - x1));
      const b = (y2 - y1 - a * (x2 * x2 - x1 * x1)) / (x2 - x1);
      const c = y1 - (a * x1 * x1 + b * x1);

      this.nodes.push(new DragTableNode(current.mach, current.drag_coefficient, a, b, c));
    }

    // Last node
    this.nodes.push(
      new DragTableNode(last.mach, last.drag_coefficient, 0.0, 0.0, last.drag_coefficient)
    );
  }

  get_node(index: number): DragTableNode {
    return this.nodes[index]!;
  }

  find_node(mach: number): { node: DragTableNode; index: number } {
    let low = 0;
    let high = this.nodes.length - 1;

    while (high - low > 1) {
      const mid = Math.floor((high + low) / 2.0);
      if (this.nodes[mid]!.mach < mach) {
        low = mid;
      } else {
        high = mid;
      }
    }

    if (this.nodes[high]!.mach - mach > mach - this.nodes[low]!.mach) {
      return { node: this.nodes[low]!, index: low };
    } else {
      return { node: this.nodes[high]!, index: high };
    }
  }

  find_lower_node(mach: number): { node: DragTableNode; index: number } {
    let { node, index } = this.find_node(mach);

    while (node.mach > mach && index > 0) {
      index -= 1;
      node = this.get_node(index);
    }

    return { node, index };
  }
}

export class DragTableStorage {
  private static table_id_to_table: Map<DragTableId, DragTable> = new Map();

  private static readonly G1_DATA_POINTS: DragTableDataPoint[] = [
    { mach: 0.0, drag_coefficient: 0.2629 },
    { mach: 0.05, drag_coefficient: 0.2558 },
    { mach: 0.1, drag_coefficient: 0.2487 },
    { mach: 0.15, drag_coefficient: 0.2413 },
    { mach: 0.2, drag_coefficient: 0.2344 },
    { mach: 0.25, drag_coefficient: 0.2278 },
    { mach: 0.3, drag_coefficient: 0.2214 },
    { mach: 0.35, drag_coefficient: 0.2155 },
    { mach: 0.4, drag_coefficient: 0.2104 },
    { mach: 0.45, drag_coefficient: 0.2061 },
    { mach: 0.5, drag_coefficient: 0.2032 },
    { mach: 0.55, drag_coefficient: 0.202 },
    { mach: 0.6, drag_coefficient: 0.2034 },
    { mach: 0.7, drag_coefficient: 0.2165 },
    { mach: 0.725, drag_coefficient: 0.223 },
    { mach: 0.75, drag_coefficient: 0.2313 },
    { mach: 0.775, drag_coefficient: 0.2417 },
    { mach: 0.8, drag_coefficient: 0.2546 },
    { mach: 0.825, drag_coefficient: 0.2706 },
    { mach: 0.85, drag_coefficient: 0.2901 },
    { mach: 0.875, drag_coefficient: 0.3136 },
    { mach: 0.9, drag_coefficient: 0.3415 },
    { mach: 0.925, drag_coefficient: 0.3734 },
    { mach: 0.95, drag_coefficient: 0.4084 },
    { mach: 0.975, drag_coefficient: 0.4448 },
    { mach: 1.0, drag_coefficient: 0.4805 },
    { mach: 1.025, drag_coefficient: 0.5136 },
    { mach: 1.05, drag_coefficient: 0.5427 },
    { mach: 1.075, drag_coefficient: 0.5677 },
    { mach: 1.1, drag_coefficient: 0.5883 },
    { mach: 1.125, drag_coefficient: 0.6053 },
    { mach: 1.15, drag_coefficient: 0.6191 },
    { mach: 1.2, drag_coefficient: 0.6393 },
    { mach: 1.25, drag_coefficient: 0.6518 },
    { mach: 1.3, drag_coefficient: 0.6589 },
    { mach: 1.35, drag_coefficient: 0.6621 },
    { mach: 1.4, drag_coefficient: 0.6625 },
    { mach: 1.45, drag_coefficient: 0.6607 },
    { mach: 1.5, drag_coefficient: 0.6573 },
    { mach: 1.55, drag_coefficient: 0.6528 },
    { mach: 1.6, drag_coefficient: 0.6474 },
    { mach: 1.65, drag_coefficient: 0.6413 },
    { mach: 1.7, drag_coefficient: 0.6347 },
    { mach: 1.75, drag_coefficient: 0.628 },
    { mach: 1.8, drag_coefficient: 0.621 },
    { mach: 1.85, drag_coefficient: 0.6141 },
    { mach: 1.9, drag_coefficient: 0.6072 },
    { mach: 1.95, drag_coefficient: 0.6003 },
    { mach: 2.0, drag_coefficient: 0.5934 },
    { mach: 2.05, drag_coefficient: 0.5867 },
    { mach: 2.1, drag_coefficient: 0.5804 },
    { mach: 2.15, drag_coefficient: 0.5743 },
    { mach: 2.2, drag_coefficient: 0.5685 },
    { mach: 2.25, drag_coefficient: 0.563 },
    { mach: 2.3, drag_coefficient: 0.5577 },
    { mach: 2.35, drag_coefficient: 0.5527 },
    { mach: 2.4, drag_coefficient: 0.5481 },
    { mach: 2.45, drag_coefficient: 0.5438 },
    { mach: 2.5, drag_coefficient: 0.5397 },
    { mach: 2.6, drag_coefficient: 0.5325 },
    { mach: 2.7, drag_coefficient: 0.5264 },
    { mach: 2.8, drag_coefficient: 0.5211 },
    { mach: 2.9, drag_coefficient: 0.5168 },
    { mach: 3.0, drag_coefficient: 0.5133 },
    { mach: 3.1, drag_coefficient: 0.5105 },
    { mach: 3.2, drag_coefficient: 0.5084 },
    { mach: 3.3, drag_coefficient: 0.5067 },
    { mach: 3.4, drag_coefficient: 0.5054 },
    { mach: 3.5, drag_coefficient: 0.504 },
    { mach: 3.6, drag_coefficient: 0.503 },
    { mach: 3.7, drag_coefficient: 0.5022 },
    { mach: 3.8, drag_coefficient: 0.5016 },
    { mach: 3.9, drag_coefficient: 0.501 },
    { mach: 4.0, drag_coefficient: 0.5006 },
    { mach: 4.2, drag_coefficient: 0.4998 },
    { mach: 4.4, drag_coefficient: 0.4995 },
    { mach: 4.6, drag_coefficient: 0.4992 },
    { mach: 4.8, drag_coefficient: 0.499 },
    { mach: 5.0, drag_coefficient: 0.4988 },
  ];

  private static readonly G2_DATA_POINTS: DragTableDataPoint[] = [
    { mach: 0.0, drag_coefficient: 0.2303 },
    { mach: 0.05, drag_coefficient: 0.2298 },
    { mach: 0.1, drag_coefficient: 0.2287 },
    { mach: 0.15, drag_coefficient: 0.2271 },
    { mach: 0.2, drag_coefficient: 0.2251 },
    { mach: 0.25, drag_coefficient: 0.2227 },
    { mach: 0.3, drag_coefficient: 0.2196 },
    { mach: 0.35, drag_coefficient: 0.2156 },
    { mach: 0.4, drag_coefficient: 0.2107 },
    { mach: 0.45, drag_coefficient: 0.2048 },
    { mach: 0.5, drag_coefficient: 0.198 },
    { mach: 0.55, drag_coefficient: 0.1905 },
    { mach: 0.6, drag_coefficient: 0.1828 },
    { mach: 0.65, drag_coefficient: 0.1758 },
    { mach: 0.7, drag_coefficient: 0.1702 },
    { mach: 0.75, drag_coefficient: 0.1669 },
    { mach: 0.775, drag_coefficient: 0.1664 },
    { mach: 0.8, drag_coefficient: 0.1667 },
    { mach: 0.825, drag_coefficient: 0.1682 },
    { mach: 0.85, drag_coefficient: 0.1711 },
    { mach: 0.875, drag_coefficient: 0.1761 },
    { mach: 0.9, drag_coefficient: 0.1831 },
    { mach: 0.925, drag_coefficient: 0.2004 },
    { mach: 0.95, drag_coefficient: 0.2589 },
    { mach: 0.975, drag_coefficient: 0.3492 },
    { mach: 1.0, drag_coefficient: 0.3983 },
    { mach: 1.025, drag_coefficient: 0.4075 },
    { mach: 1.05, drag_coefficient: 0.4103 },
    { mach: 1.075, drag_coefficient: 0.4114 },
    { mach: 1.1, drag_coefficient: 0.4106 },
    { mach: 1.125, drag_coefficient: 0.4089 },
    { mach: 1.15, drag_coefficient: 0.4068 },
    { mach: 1.175, drag_coefficient: 0.4046 },
    { mach: 1.2, drag_coefficient: 0.4021 },
    { mach: 1.25, drag_coefficient: 0.3966 },
    { mach: 1.3, drag_coefficient: 0.3904 },
    { mach: 1.35, drag_coefficient: 0.3835 },
    { mach: 1.4, drag_coefficient: 0.3759 },
    { mach: 1.45, drag_coefficient: 0.3678 },
    { mach: 1.5, drag_coefficient: 0.3594 },
    { mach: 1.55, drag_coefficient: 0.3512 },
    { mach: 1.6, drag_coefficient: 0.3432 },
    { mach: 1.65, drag_coefficient: 0.3356 },
    { mach: 1.7, drag_coefficient: 0.3282 },
    { mach: 1.75, drag_coefficient: 0.3213 },
    { mach: 1.8, drag_coefficient: 0.3149 },
    { mach: 1.85, drag_coefficient: 0.3089 },
    { mach: 1.9, drag_coefficient: 0.3033 },
    { mach: 1.95, drag_coefficient: 0.2982 },
    { mach: 2.0, drag_coefficient: 0.2933 },
    { mach: 2.05, drag_coefficient: 0.2889 },
    { mach: 2.1, drag_coefficient: 0.2846 },
    { mach: 2.15, drag_coefficient: 0.2806 },
    { mach: 2.2, drag_coefficient: 0.2768 },
    { mach: 2.25, drag_coefficient: 0.2731 },
    { mach: 2.3, drag_coefficient: 0.2696 },
    { mach: 2.35, drag_coefficient: 0.2663 },
    { mach: 2.4, drag_coefficient: 0.2632 },
    { mach: 2.45, drag_coefficient: 0.2602 },
    { mach: 2.5, drag_coefficient: 0.2572 },
    { mach: 2.55, drag_coefficient: 0.2543 },
    { mach: 2.6, drag_coefficient: 0.2515 },
    { mach: 2.65, drag_coefficient: 0.2487 },
    { mach: 2.7, drag_coefficient: 0.246 },
    { mach: 2.75, drag_coefficient: 0.2433 },
    { mach: 2.8, drag_coefficient: 0.2408 },
    { mach: 2.85, drag_coefficient: 0.2382 },
    { mach: 2.9, drag_coefficient: 0.2357 },
    { mach: 2.95, drag_coefficient: 0.2333 },
    { mach: 3.0, drag_coefficient: 0.2309 },
    { mach: 3.1, drag_coefficient: 0.2262 },
    { mach: 3.2, drag_coefficient: 0.2217 },
    { mach: 3.3, drag_coefficient: 0.2173 },
    { mach: 3.4, drag_coefficient: 0.2132 },
    { mach: 3.5, drag_coefficient: 0.2091 },
    { mach: 3.6, drag_coefficient: 0.2052 },
    { mach: 3.7, drag_coefficient: 0.2014 },
    { mach: 3.8, drag_coefficient: 0.1978 },
    { mach: 3.9, drag_coefficient: 0.1944 },
    { mach: 4.0, drag_coefficient: 0.1912 },
    { mach: 4.2, drag_coefficient: 0.1851 },
    { mach: 4.4, drag_coefficient: 0.1794 },
    { mach: 4.6, drag_coefficient: 0.1741 },
    { mach: 4.8, drag_coefficient: 0.1693 },
    { mach: 5.0, drag_coefficient: 0.1648 },
  ];

  private static readonly G5_DATA_POINTS: DragTableDataPoint[] = [
    { mach: 0.0, drag_coefficient: 0.171 },
    { mach: 0.05, drag_coefficient: 0.1719 },
    { mach: 0.1, drag_coefficient: 0.1727 },
    { mach: 0.15, drag_coefficient: 0.1732 },
    { mach: 0.2, drag_coefficient: 0.1734 },
    { mach: 0.25, drag_coefficient: 0.173 },
    { mach: 0.3, drag_coefficient: 0.1718 },
    { mach: 0.35, drag_coefficient: 0.1696 },
    { mach: 0.4, drag_coefficient: 0.1668 },
    { mach: 0.45, drag_coefficient: 0.1637 },
    { mach: 0.5, drag_coefficient: 0.1603 },
    { mach: 0.55, drag_coefficient: 0.1566 },
    { mach: 0.6, drag_coefficient: 0.1529 },
    { mach: 0.65, drag_coefficient: 0.1497 },
    { mach: 0.7, drag_coefficient: 0.1473 },
    { mach: 0.75, drag_coefficient: 0.1463 },
    { mach: 0.8, drag_coefficient: 0.1489 },
    { mach: 0.85, drag_coefficient: 0.1583 },
    { mach: 0.875, drag_coefficient: 0.1672 },
    { mach: 0.9, drag_coefficient: 0.1815 },
    { mach: 0.925, drag_coefficient: 0.2051 },
    { mach: 0.95, drag_coefficient: 0.2413 },
    { mach: 0.975, drag_coefficient: 0.2884 },
    { mach: 1.0, drag_coefficient: 0.3379 },
    { mach: 1.025, drag_coefficient: 0.3785 },
    { mach: 1.05, drag_coefficient: 0.4032 },
    { mach: 1.075, drag_coefficient: 0.4147 },
    { mach: 1.1, drag_coefficient: 0.4201 },
    { mach: 1.15, drag_coefficient: 0.4278 },
    { mach: 1.2, drag_coefficient: 0.4338 },
    { mach: 1.25, drag_coefficient: 0.4373 },
    { mach: 1.3, drag_coefficient: 0.4392 },
    { mach: 1.35, drag_coefficient: 0.4403 },
    { mach: 1.4, drag_coefficient: 0.4406 },
    { mach: 1.45, drag_coefficient: 0.4401 },
    { mach: 1.5, drag_coefficient: 0.4386 },
    { mach: 1.55, drag_coefficient: 0.4362 },
    { mach: 1.6, drag_coefficient: 0.4328 },
    { mach: 1.65, drag_coefficient: 0.4286 },
    { mach: 1.7, drag_coefficient: 0.4237 },
    { mach: 1.75, drag_coefficient: 0.4182 },
    { mach: 1.8, drag_coefficient: 0.4121 },
    { mach: 1.85, drag_coefficient: 0.4057 },
    { mach: 1.9, drag_coefficient: 0.3991 },
    { mach: 1.95, drag_coefficient: 0.3926 },
    { mach: 2.0, drag_coefficient: 0.3861 },
    { mach: 2.05, drag_coefficient: 0.38 },
    { mach: 2.1, drag_coefficient: 0.3741 },
    { mach: 2.15, drag_coefficient: 0.3684 },
    { mach: 2.2, drag_coefficient: 0.363 },
    { mach: 2.25, drag_coefficient: 0.3578 },
    { mach: 2.3, drag_coefficient: 0.3529 },
    { mach: 2.35, drag_coefficient: 0.3481 },
    { mach: 2.4, drag_coefficient: 0.3435 },
    { mach: 2.45, drag_coefficient: 0.3391 },
    { mach: 2.5, drag_coefficient: 0.3349 },
    { mach: 2.6, drag_coefficient: 0.3269 },
    { mach: 2.7, drag_coefficient: 0.3194 },
    { mach: 2.8, drag_coefficient: 0.3125 },
    { mach: 2.9, drag_coefficient: 0.306 },
    { mach: 3.0, drag_coefficient: 0.2999 },
    { mach: 3.1, drag_coefficient: 0.2942 },
    { mach: 3.2, drag_coefficient: 0.2889 },
    { mach: 3.3, drag_coefficient: 0.2838 },
    { mach: 3.4, drag_coefficient: 0.279 },
    { mach: 3.5, drag_coefficient: 0.2745 },
    { mach: 3.6, drag_coefficient: 0.2703 },
    { mach: 3.7, drag_coefficient: 0.2662 },
    { mach: 3.8, drag_coefficient: 0.2624 },
    { mach: 3.9, drag_coefficient: 0.2588 },
    { mach: 4.0, drag_coefficient: 0.2553 },
    { mach: 4.2, drag_coefficient: 0.2488 },
    { mach: 4.4, drag_coefficient: 0.2429 },
    { mach: 4.6, drag_coefficient: 0.2376 },
    { mach: 4.8, drag_coefficient: 0.2326 },
    { mach: 5.0, drag_coefficient: 0.228 },
  ];

  private static readonly G6_DATA_POINTS: DragTableDataPoint[] = [
    { mach: 0.0, drag_coefficient: 0.2617 },
    { mach: 0.05, drag_coefficient: 0.2553 },
    { mach: 0.1, drag_coefficient: 0.2491 },
    { mach: 0.15, drag_coefficient: 0.2432 },
    { mach: 0.2, drag_coefficient: 0.2376 },
    { mach: 0.25, drag_coefficient: 0.2324 },
    { mach: 0.3, drag_coefficient: 0.2278 },
    { mach: 0.35, drag_coefficient: 0.2238 },
    { mach: 0.4, drag_coefficient: 0.2205 },
    { mach: 0.45, drag_coefficient: 0.2177 },
    { mach: 0.5, drag_coefficient: 0.2155 },
    { mach: 0.55, drag_coefficient: 0.2138 },
    { mach: 0.6, drag_coefficient: 0.2126 },
    { mach: 0.65, drag_coefficient: 0.2121 },
    { mach: 0.7, drag_coefficient: 0.2122 },
    { mach: 0.75, drag_coefficient: 0.2132 },
    { mach: 0.8, drag_coefficient: 0.2154 },
    { mach: 0.85, drag_coefficient: 0.2194 },
    { mach: 0.875, drag_coefficient: 0.2229 },
    { mach: 0.9, drag_coefficient: 0.2297 },
    { mach: 0.925, drag_coefficient: 0.2449 },
    { mach: 0.95, drag_coefficient: 0.2732 },
    { mach: 0.975, drag_coefficient: 0.3141 },
    { mach: 1.0, drag_coefficient: 0.3597 },
    { mach: 1.025, drag_coefficient: 0.3994 },
    { mach: 1.05, drag_coefficient: 0.4261 },
    { mach: 1.075, drag_coefficient: 0.4402 },
    { mach: 1.1, drag_coefficient: 0.4465 },
    { mach: 1.125, drag_coefficient: 0.449 },
    { mach: 1.15, drag_coefficient: 0.4497 },
    { mach: 1.175, drag_coefficient: 0.4494 },
    { mach: 1.2, drag_coefficient: 0.4482 },
    { mach: 1.225, drag_coefficient: 0.4464 },
    { mach: 1.25, drag_coefficient: 0.4441 },
    { mach: 1.3, drag_coefficient: 0.439 },
    { mach: 1.35, drag_coefficient: 0.4336 },
    { mach: 1.4, drag_coefficient: 0.4279 },
    { mach: 1.45, drag_coefficient: 0.4221 },
    { mach: 1.5, drag_coefficient: 0.4162 },
    { mach: 1.55, drag_coefficient: 0.4102 },
    { mach: 1.6, drag_coefficient: 0.4042 },
    { mach: 1.65, drag_coefficient: 0.3981 },
    { mach: 1.7, drag_coefficient: 0.3919 },
    { mach: 1.75, drag_coefficient: 0.3855 },
    { mach: 1.8, drag_coefficient: 0.3788 },
    { mach: 1.85, drag_coefficient: 0.3721 },
    { mach: 1.9, drag_coefficient: 0.3652 },
    { mach: 1.95, drag_coefficient: 0.3583 },
    { mach: 2.0, drag_coefficient: 0.3515 },
    { mach: 2.05, drag_coefficient: 0.3447 },
    { mach: 2.1, drag_coefficient: 0.3381 },
    { mach: 2.15, drag_coefficient: 0.3314 },
    { mach: 2.2, drag_coefficient: 0.3249 },
    { mach: 2.25, drag_coefficient: 0.3185 },
    { mach: 2.3, drag_coefficient: 0.3122 },
    { mach: 2.35, drag_coefficient: 0.306 },
    { mach: 2.4, drag_coefficient: 0.3 },
    { mach: 2.45, drag_coefficient: 0.2941 },
    { mach: 2.5, drag_coefficient: 0.2883 },
    { mach: 2.6, drag_coefficient: 0.2772 },
    { mach: 2.7, drag_coefficient: 0.2668 },
    { mach: 2.8, drag_coefficient: 0.2574 },
    { mach: 2.9, drag_coefficient: 0.2487 },
    { mach: 3.0, drag_coefficient: 0.2407 },
    { mach: 3.1, drag_coefficient: 0.2333 },
    { mach: 3.2, drag_coefficient: 0.2265 },
    { mach: 3.3, drag_coefficient: 0.2202 },
    { mach: 3.4, drag_coefficient: 0.2144 },
    { mach: 3.5, drag_coefficient: 0.2089 },
    { mach: 3.6, drag_coefficient: 0.2039 },
    { mach: 3.7, drag_coefficient: 0.1991 },
    { mach: 3.8, drag_coefficient: 0.1947 },
    { mach: 3.9, drag_coefficient: 0.1905 },
    { mach: 4.0, drag_coefficient: 0.1866 },
    { mach: 4.2, drag_coefficient: 0.1794 },
    { mach: 4.4, drag_coefficient: 0.173 },
    { mach: 4.6, drag_coefficient: 0.1673 },
    { mach: 4.8, drag_coefficient: 0.1621 },
    { mach: 5.0, drag_coefficient: 0.1574 },
  ];

  private static readonly G7_DATA_POINTS: DragTableDataPoint[] = [
    { mach: 0.0, drag_coefficient: 0.1198 },
    { mach: 0.05, drag_coefficient: 0.1197 },
    { mach: 0.1, drag_coefficient: 0.1196 },
    { mach: 0.15, drag_coefficient: 0.1194 },
    { mach: 0.2, drag_coefficient: 0.1193 },
    { mach: 0.25, drag_coefficient: 0.1194 },
    { mach: 0.3, drag_coefficient: 0.1194 },
    { mach: 0.35, drag_coefficient: 0.1194 },
    { mach: 0.4, drag_coefficient: 0.1193 },
    { mach: 0.45, drag_coefficient: 0.1193 },
    { mach: 0.5, drag_coefficient: 0.1194 },
    { mach: 0.55, drag_coefficient: 0.1193 },
    { mach: 0.6, drag_coefficient: 0.1194 },
    { mach: 0.65, drag_coefficient: 0.1197 },
    { mach: 0.7, drag_coefficient: 0.1202 },
    { mach: 0.725, drag_coefficient: 0.1207 },
    { mach: 0.75, drag_coefficient: 0.1215 },
    { mach: 0.775, drag_coefficient: 0.1226 },
    { mach: 0.8, drag_coefficient: 0.1242 },
    { mach: 0.825, drag_coefficient: 0.1266 },
    { mach: 0.85, drag_coefficient: 0.1306 },
    { mach: 0.875, drag_coefficient: 0.1368 },
    { mach: 0.9, drag_coefficient: 0.1464 },
    { mach: 0.925, drag_coefficient: 0.166 },
    { mach: 0.95, drag_coefficient: 0.2054 },
    { mach: 0.975, drag_coefficient: 0.2993 },
    { mach: 1.0, drag_coefficient: 0.3803 },
    { mach: 1.025, drag_coefficient: 0.4015 },
    { mach: 1.05, drag_coefficient: 0.4043 },
    { mach: 1.075, drag_coefficient: 0.4034 },
    { mach: 1.1, drag_coefficient: 0.4014 },
    { mach: 1.125, drag_coefficient: 0.3987 },
    { mach: 1.15, drag_coefficient: 0.3955 },
    { mach: 1.2, drag_coefficient: 0.3884 },
    { mach: 1.25, drag_coefficient: 0.381 },
    { mach: 1.3, drag_coefficient: 0.3732 },
    { mach: 1.35, drag_coefficient: 0.3657 },
    { mach: 1.4, drag_coefficient: 0.358 },
    { mach: 1.5, drag_coefficient: 0.344 },
    { mach: 1.55, drag_coefficient: 0.3376 },
    { mach: 1.6, drag_coefficient: 0.3315 },
    { mach: 1.65, drag_coefficient: 0.326 },
    { mach: 1.7, drag_coefficient: 0.3209 },
    { mach: 1.75, drag_coefficient: 0.316 },
    { mach: 1.8, drag_coefficient: 0.3117 },
    { mach: 1.85, drag_coefficient: 0.3078 },
    { mach: 1.9, drag_coefficient: 0.3042 },
    { mach: 1.95, drag_coefficient: 0.301 },
    { mach: 2.0, drag_coefficient: 0.298 },
    { mach: 2.05, drag_coefficient: 0.2951 },
    { mach: 2.1, drag_coefficient: 0.2922 },
    { mach: 2.15, drag_coefficient: 0.2892 },
    { mach: 2.2, drag_coefficient: 0.2864 },
    { mach: 2.25, drag_coefficient: 0.2835 },
    { mach: 2.3, drag_coefficient: 0.2807 },
    { mach: 2.35, drag_coefficient: 0.2779 },
    { mach: 2.4, drag_coefficient: 0.2752 },
    { mach: 2.45, drag_coefficient: 0.2725 },
    { mach: 2.5, drag_coefficient: 0.2697 },
    { mach: 2.55, drag_coefficient: 0.267 },
    { mach: 2.6, drag_coefficient: 0.2643 },
    { mach: 2.65, drag_coefficient: 0.2615 },
    { mach: 2.7, drag_coefficient: 0.2588 },
    { mach: 2.75, drag_coefficient: 0.2561 },
    { mach: 2.8, drag_coefficient: 0.2533 },
    { mach: 2.85, drag_coefficient: 0.2506 },
    { mach: 2.9, drag_coefficient: 0.2479 },
    { mach: 2.95, drag_coefficient: 0.2451 },
    { mach: 3.0, drag_coefficient: 0.2424 },
    { mach: 3.1, drag_coefficient: 0.2368 },
    { mach: 3.2, drag_coefficient: 0.2313 },
    { mach: 3.3, drag_coefficient: 0.2258 },
    { mach: 3.4, drag_coefficient: 0.2205 },
    { mach: 3.5, drag_coefficient: 0.2154 },
    { mach: 3.6, drag_coefficient: 0.2106 },
    { mach: 3.7, drag_coefficient: 0.206 },
    { mach: 3.8, drag_coefficient: 0.2017 },
    { mach: 3.9, drag_coefficient: 0.1975 },
    { mach: 4.0, drag_coefficient: 0.1935 },
    { mach: 4.2, drag_coefficient: 0.1861 },
    { mach: 4.4, drag_coefficient: 0.1793 },
    { mach: 4.6, drag_coefficient: 0.173 },
    { mach: 4.8, drag_coefficient: 0.1672 },
    { mach: 5.0, drag_coefficient: 0.1618 },
  ];

  private static readonly G8_DATA_POINTS: DragTableDataPoint[] = [
    { mach: 0.0, drag_coefficient: 0.2105 },
    { mach: 0.05, drag_coefficient: 0.2105 },
    { mach: 0.1, drag_coefficient: 0.2104 },
    { mach: 0.15, drag_coefficient: 0.2104 },
    { mach: 0.2, drag_coefficient: 0.2103 },
    { mach: 0.25, drag_coefficient: 0.2103 },
    { mach: 0.3, drag_coefficient: 0.2103 },
    { mach: 0.35, drag_coefficient: 0.2103 },
    { mach: 0.4, drag_coefficient: 0.2103 },
    { mach: 0.45, drag_coefficient: 0.2102 },
    { mach: 0.5, drag_coefficient: 0.2102 },
    { mach: 0.55, drag_coefficient: 0.2102 },
    { mach: 0.6, drag_coefficient: 0.2102 },
    { mach: 0.65, drag_coefficient: 0.2102 },
    { mach: 0.7, drag_coefficient: 0.2103 },
    { mach: 0.75, drag_coefficient: 0.2103 },
    { mach: 0.8, drag_coefficient: 0.2104 },
    { mach: 0.825, drag_coefficient: 0.2104 },
    { mach: 0.85, drag_coefficient: 0.2105 },
    { mach: 0.875, drag_coefficient: 0.2106 },
    { mach: 0.9, drag_coefficient: 0.2109 },
    { mach: 0.925, drag_coefficient: 0.2183 },
    { mach: 0.95, drag_coefficient: 0.2571 },
    { mach: 0.975, drag_coefficient: 0.3358 },
    { mach: 1.0, drag_coefficient: 0.4068 },
    { mach: 1.025, drag_coefficient: 0.4378 },
    { mach: 1.05, drag_coefficient: 0.4476 },
    { mach: 1.075, drag_coefficient: 0.4493 },
    { mach: 1.1, drag_coefficient: 0.4477 },
    { mach: 1.125, drag_coefficient: 0.445 },
    { mach: 1.15, drag_coefficient: 0.4419 },
    { mach: 1.2, drag_coefficient: 0.4353 },
    { mach: 1.25, drag_coefficient: 0.4283 },
    { mach: 1.3, drag_coefficient: 0.4208 },
    { mach: 1.35, drag_coefficient: 0.4133 },
    { mach: 1.4, drag_coefficient: 0.4059 },
    { mach: 1.45, drag_coefficient: 0.3986 },
    { mach: 1.5, drag_coefficient: 0.3915 },
    { mach: 1.55, drag_coefficient: 0.3845 },
    { mach: 1.6, drag_coefficient: 0.3777 },
    { mach: 1.65, drag_coefficient: 0.371 },
    { mach: 1.7, drag_coefficient: 0.3645 },
    { mach: 1.75, drag_coefficient: 0.3581 },
    { mach: 1.8, drag_coefficient: 0.3519 },
    { mach: 1.85, drag_coefficient: 0.3458 },
    { mach: 1.9, drag_coefficient: 0.34 },
    { mach: 1.95, drag_coefficient: 0.3343 },
    { mach: 2.0, drag_coefficient: 0.3288 },
    { mach: 2.05, drag_coefficient: 0.3234 },
    { mach: 2.1, drag_coefficient: 0.3182 },
    { mach: 2.15, drag_coefficient: 0.3131 },
    { mach: 2.2, drag_coefficient: 0.3081 },
    { mach: 2.25, drag_coefficient: 0.3032 },
    { mach: 2.3, drag_coefficient: 0.2983 },
    { mach: 2.35, drag_coefficient: 0.2937 },
    { mach: 2.4, drag_coefficient: 0.2891 },
    { mach: 2.45, drag_coefficient: 0.2845 },
    { mach: 2.5, drag_coefficient: 0.2802 },
    { mach: 2.6, drag_coefficient: 0.272 },
    { mach: 2.7, drag_coefficient: 0.2642 },
    { mach: 2.8, drag_coefficient: 0.2569 },
    { mach: 2.9, drag_coefficient: 0.2499 },
    { mach: 3.0, drag_coefficient: 0.2432 },
    { mach: 3.1, drag_coefficient: 0.2368 },
    { mach: 3.2, drag_coefficient: 0.2308 },
    { mach: 3.3, drag_coefficient: 0.2251 },
    { mach: 3.4, drag_coefficient: 0.2197 },
    { mach: 3.5, drag_coefficient: 0.2147 },
    { mach: 3.6, drag_coefficient: 0.2101 },
    { mach: 3.7, drag_coefficient: 0.2058 },
    { mach: 3.8, drag_coefficient: 0.2019 },
    { mach: 3.9, drag_coefficient: 0.1983 },
    { mach: 4.0, drag_coefficient: 0.195 },
    { mach: 4.2, drag_coefficient: 0.189 },
    { mach: 4.4, drag_coefficient: 0.1837 },
    { mach: 4.6, drag_coefficient: 0.1791 },
    { mach: 4.8, drag_coefficient: 0.175 },
    { mach: 5.0, drag_coefficient: 0.1713 },
  ];

  private static readonly GI_DATA_POINTS: DragTableDataPoint[] = [
    { mach: 0.0, drag_coefficient: 0.2282 },
    { mach: 0.05, drag_coefficient: 0.2282 },
    { mach: 0.1, drag_coefficient: 0.2282 },
    { mach: 0.15, drag_coefficient: 0.2282 },
    { mach: 0.2, drag_coefficient: 0.2282 },
    { mach: 0.25, drag_coefficient: 0.2282 },
    { mach: 0.3, drag_coefficient: 0.2282 },
    { mach: 0.35, drag_coefficient: 0.2282 },
    { mach: 0.4, drag_coefficient: 0.2282 },
    { mach: 0.45, drag_coefficient: 0.2282 },
    { mach: 0.5, drag_coefficient: 0.2282 },
    { mach: 0.55, drag_coefficient: 0.2282 },
    { mach: 0.6, drag_coefficient: 0.2282 },
    { mach: 0.65, drag_coefficient: 0.2282 },
    { mach: 0.7, drag_coefficient: 0.2282 },
    { mach: 0.725, drag_coefficient: 0.2353 },
    { mach: 0.75, drag_coefficient: 0.2434 },
    { mach: 0.775, drag_coefficient: 0.2515 },
    { mach: 0.8, drag_coefficient: 0.2596 },
    { mach: 0.825, drag_coefficient: 0.2677 },
    { mach: 0.85, drag_coefficient: 0.2759 },
    { mach: 0.875, drag_coefficient: 0.2913 },
    { mach: 0.9, drag_coefficient: 0.317 },
    { mach: 0.925, drag_coefficient: 0.3442 },
    { mach: 0.95, drag_coefficient: 0.3728 },
    { mach: 1.0, drag_coefficient: 0.4349 },
    { mach: 1.05, drag_coefficient: 0.5034 },
    { mach: 1.075, drag_coefficient: 0.5402 },
    { mach: 1.1, drag_coefficient: 0.5756 },
    { mach: 1.125, drag_coefficient: 0.5887 },
    { mach: 1.15, drag_coefficient: 0.6018 },
    { mach: 1.175, drag_coefficient: 0.6149 },
    { mach: 1.2, drag_coefficient: 0.6279 },
    { mach: 1.225, drag_coefficient: 0.6418 },
    { mach: 1.25, drag_coefficient: 0.6423 },
    { mach: 1.3, drag_coefficient: 0.6423 },
    { mach: 1.35, drag_coefficient: 0.6423 },
    { mach: 1.4, drag_coefficient: 0.6423 },
    { mach: 1.45, drag_coefficient: 0.6423 },
    { mach: 1.5, drag_coefficient: 0.6423 },
    { mach: 1.55, drag_coefficient: 0.6423 },
    { mach: 1.6, drag_coefficient: 0.6423 },
    { mach: 1.625, drag_coefficient: 0.6407 },
    { mach: 1.65, drag_coefficient: 0.6378 },
    { mach: 1.7, drag_coefficient: 0.6321 },
    { mach: 1.75, drag_coefficient: 0.6266 },
    { mach: 1.8, drag_coefficient: 0.6213 },
    { mach: 1.85, drag_coefficient: 0.6163 },
    { mach: 1.9, drag_coefficient: 0.6113 },
    { mach: 1.95, drag_coefficient: 0.6066 },
    { mach: 2.0, drag_coefficient: 0.602 },
    { mach: 2.05, drag_coefficient: 0.5976 },
    { mach: 2.1, drag_coefficient: 0.5933 },
    { mach: 2.15, drag_coefficient: 0.5891 },
    { mach: 2.2, drag_coefficient: 0.585 },
    { mach: 2.25, drag_coefficient: 0.5811 },
    { mach: 2.3, drag_coefficient: 0.5773 },
    { mach: 2.35, drag_coefficient: 0.5733 },
    { mach: 2.4, drag_coefficient: 0.5679 },
    { mach: 2.45, drag_coefficient: 0.5626 },
    { mach: 2.5, drag_coefficient: 0.5576 },
    { mach: 2.6, drag_coefficient: 0.5478 },
    { mach: 2.7, drag_coefficient: 0.5386 },
    { mach: 2.8, drag_coefficient: 0.5298 },
    { mach: 2.9, drag_coefficient: 0.5215 },
    { mach: 3.0, drag_coefficient: 0.5136 },
    { mach: 3.1, drag_coefficient: 0.5061 },
    { mach: 3.2, drag_coefficient: 0.4989 },
    { mach: 3.3, drag_coefficient: 0.4921 },
    { mach: 3.4, drag_coefficient: 0.4855 },
    { mach: 3.5, drag_coefficient: 0.4792 },
    { mach: 3.6, drag_coefficient: 0.4732 },
    { mach: 3.7, drag_coefficient: 0.4674 },
    { mach: 3.8, drag_coefficient: 0.4618 },
    { mach: 3.9, drag_coefficient: 0.4564 },
    { mach: 4.0, drag_coefficient: 0.4513 },
    { mach: 4.2, drag_coefficient: 0.4415 },
    { mach: 4.4, drag_coefficient: 0.4323 },
    { mach: 4.6, drag_coefficient: 0.4238 },
    { mach: 4.8, drag_coefficient: 0.4157 },
    { mach: 5.0, drag_coefficient: 0.4082 },
  ];

  private static readonly RA4_DATA_POINTS: DragTableDataPoint[] = [
    { mach: 0.0, drag_coefficient: 0.2283 },
    { mach: 0.05, drag_coefficient: 0.2283 },
    { mach: 0.1, drag_coefficient: 0.2282 },
    { mach: 0.15, drag_coefficient: 0.2281 },
    { mach: 0.2, drag_coefficient: 0.2281 },
    { mach: 0.25, drag_coefficient: 0.2281 },
    { mach: 0.3, drag_coefficient: 0.2281 },
    { mach: 0.35, drag_coefficient: 0.2281 },
    { mach: 0.4, drag_coefficient: 0.2281 },
    { mach: 0.45, drag_coefficient: 0.2281 },
    { mach: 0.5, drag_coefficient: 0.2281 },
    { mach: 0.55, drag_coefficient: 0.2281 },
    { mach: 0.6, drag_coefficient: 0.2281 },
    { mach: 0.65, drag_coefficient: 0.2281 },
    { mach: 0.7, drag_coefficient: 0.2288 },
    { mach: 0.725, drag_coefficient: 0.2296 },
    { mach: 0.75, drag_coefficient: 0.2307 },
    { mach: 0.775, drag_coefficient: 0.232 },
    { mach: 0.8, drag_coefficient: 0.2334 },
    { mach: 0.825, drag_coefficient: 0.2359 },
    { mach: 0.85, drag_coefficient: 0.2389 },
    { mach: 0.875, drag_coefficient: 0.248 },
    { mach: 0.9, drag_coefficient: 0.2604 },
    { mach: 0.925, drag_coefficient: 0.2819 },
    { mach: 0.95, drag_coefficient: 0.3111 },
    { mach: 0.975, drag_coefficient: 0.3496 },
    { mach: 1.0, drag_coefficient: 0.3975 },
    { mach: 1.025, drag_coefficient: 0.453 },
    { mach: 1.05, drag_coefficient: 0.501 },
    { mach: 1.075, drag_coefficient: 0.5476 },
    { mach: 1.1, drag_coefficient: 0.5719 },
    { mach: 1.125, drag_coefficient: 0.5895 },
    { mach: 1.15, drag_coefficient: 0.5943 },
    { mach: 1.175, drag_coefficient: 0.5933 },
    { mach: 1.2, drag_coefficient: 0.5881 },
    { mach: 1.225, drag_coefficient: 0.581 },
    { mach: 1.25, drag_coefficient: 0.5736 },
    { mach: 1.275, drag_coefficient: 0.569 },
    { mach: 1.3, drag_coefficient: 0.5651 },
    { mach: 1.325, drag_coefficient: 0.5629 },
    { mach: 1.35, drag_coefficient: 0.5609 },
    { mach: 1.375, drag_coefficient: 0.5591 },
    { mach: 1.4, drag_coefficient: 0.5575 },
    { mach: 1.425, drag_coefficient: 0.5558 },
    { mach: 1.45, drag_coefficient: 0.5543 },
    { mach: 1.475, drag_coefficient: 0.5527 },
    { mach: 1.5, drag_coefficient: 0.5513 },
    { mach: 1.525, drag_coefficient: 0.5499 },
    { mach: 1.55, drag_coefficient: 0.5485 },
    { mach: 1.575, drag_coefficient: 0.5472 },
    { mach: 1.6, drag_coefficient: 0.546 },
    { mach: 1.625, drag_coefficient: 0.5449 },
    { mach: 1.65, drag_coefficient: 0.5438 },
    { mach: 1.675, drag_coefficient: 0.5428 },
    { mach: 1.7, drag_coefficient: 0.5419 },
    { mach: 1.725, drag_coefficient: 0.541 },
    { mach: 1.75, drag_coefficient: 0.5401 },
    { mach: 1.775, drag_coefficient: 0.5393 },
    { mach: 1.8, drag_coefficient: 0.5385 },
    { mach: 1.825, drag_coefficient: 0.5377 },
    { mach: 1.85, drag_coefficient: 0.5369 },
    { mach: 1.875, drag_coefficient: 0.5361 },
    { mach: 1.9, drag_coefficient: 0.5354 },
    { mach: 1.925, drag_coefficient: 0.5346 },
    { mach: 1.95, drag_coefficient: 0.5338 },
    { mach: 2.0, drag_coefficient: 0.5323 },
    { mach: 2.1, drag_coefficient: 0.5294 },
    { mach: 2.2, drag_coefficient: 0.5267 },
    { mach: 2.3, drag_coefficient: 0.524 },
    { mach: 2.4, drag_coefficient: 0.5216 },
    { mach: 2.5, drag_coefficient: 0.5193 },
    { mach: 2.6, drag_coefficient: 0.517 },
    { mach: 2.65, drag_coefficient: 0.516 },
    { mach: 2.7, drag_coefficient: 0.5149 },
    { mach: 2.8, drag_coefficient: 0.5129 },
    { mach: 2.9, drag_coefficient: 0.5109 },
    { mach: 3.0, drag_coefficient: 0.5091 },
    { mach: 3.1, drag_coefficient: 0.5074 },
    { mach: 3.2, drag_coefficient: 0.5058 },
    { mach: 3.3, drag_coefficient: 0.5043 },
    { mach: 3.4, drag_coefficient: 0.5029 },
    { mach: 3.5, drag_coefficient: 0.5017 },
    { mach: 3.6, drag_coefficient: 0.5006 },
    { mach: 3.7, drag_coefficient: 0.4995 },
    { mach: 3.8, drag_coefficient: 0.4986 },
    { mach: 3.9, drag_coefficient: 0.4977 },
    { mach: 4.0, drag_coefficient: 0.4969 },
  ];

  static {
    // Initialize G1 table
    const g1DragTable = new DragTable(DragTableId.G1, DragTableStorage.G1_DATA_POINTS);
    DragTableStorage.table_id_to_table.set(DragTableId.G1, g1DragTable);

    // Initialize G2 table
    const g2DragTable = new DragTable(DragTableId.G2, DragTableStorage.G2_DATA_POINTS);
    DragTableStorage.table_id_to_table.set(DragTableId.G2, g2DragTable);

    // Initialize G5 table
    const g5DragTable = new DragTable(DragTableId.G5, DragTableStorage.G5_DATA_POINTS);
    DragTableStorage.table_id_to_table.set(DragTableId.G5, g5DragTable);

    // Initialize G6 table
    const g6DragTable = new DragTable(DragTableId.G6, DragTableStorage.G6_DATA_POINTS);
    DragTableStorage.table_id_to_table.set(DragTableId.G6, g6DragTable);

    // Initialize G7 table
    const g7DragTable = new DragTable(DragTableId.G7, DragTableStorage.G7_DATA_POINTS);
    DragTableStorage.table_id_to_table.set(DragTableId.G7, g7DragTable);

    // Initialize G8 table
    const g8DragTable = new DragTable(DragTableId.G8, DragTableStorage.G8_DATA_POINTS);
    DragTableStorage.table_id_to_table.set(DragTableId.G8, g8DragTable);

    // Initialize GI table
    const giDragTable = new DragTable(DragTableId.GI, DragTableStorage.GI_DATA_POINTS);
    DragTableStorage.table_id_to_table.set(DragTableId.GI, giDragTable);

    // Initialize RA4 table
    const ra4DragTable = new DragTable(DragTableId.RA4, DragTableStorage.RA4_DATA_POINTS);
    DragTableStorage.table_id_to_table.set(DragTableId.RA4, ra4DragTable);
  }

  static get_drag_table(id: DragTableId): DragTable {
    const table = DragTableStorage.table_id_to_table.get(id);
    if (!table) {
      throw new Error(`No drag table found for given id: ${id}`);
    }
    return table;
  }
}
